import { finalize, Observable, take } from 'rxjs';
import { AbstractBaseComponent } from '../abstracts/abstract-base.component';
import { ofType } from '@ngrx/effects';
import { ActionCreator } from '@ngrx/store';

export function HandleObservableLoading() {
  return function<T extends AbstractBaseComponent>(
    target: Object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(...args: any[]) => Observable<any> | any>
  ): TypedPropertyDescriptor<(...args: any[]) => Observable<any> | any> {
    const originalMethod = descriptor.value!;
    descriptor.value = function(this: T, ...args: any[]): Observable<any> | any {
      const result = originalMethod.apply(this, args);
      if (!result) console.warn('The method does not return an Observable');
      if (result instanceof Observable) {
        this.state.loading.set(true);
        this.logger.debug(this.name, this.uuid, 'Start observable loading', propertyKey.toString());
        const observable = result.pipe(finalize(() => {
          this.state.loading.set(false);
          this.logger.debug(this.name, this.uuid, 'Stop observable loading', propertyKey.toString());
        }));
        observable.subscribe();
        return observable;
      }
      // If the method does not return an Observable, return the result
      return result;
    };
    return descriptor;
  };
}

/**
 * Turns the component's loading flag on when the decorated dispatch runs, and off on
 * the first terminal action.
 *
 * `alsoStopsOn` covers effects with more than two outcomes — e.g. a session update that
 * can also come back "needs confirmation" (428). Without listing that third action here
 * the flag would never clear.
 */
export function HandleEffectLoading(
  success: ActionCreator,
  failure: ActionCreator,
  ...alsoStopsOn: ActionCreator[]
) {
  return function<T extends AbstractBaseComponent>(
    target: Object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(...args: any[]) => void>
  ): TypedPropertyDescriptor<(...args: any[]) => void> {
    const originalMethod = descriptor.value!;
    descriptor.value = function(this: T, ...args: any[]): void {
      // Set loading to true when dispatching
      this.state.loading.set(true);
      this.logger.debug(this.name, this.uuid, 'Start effect loading', propertyKey.toString());
      // Listen for success/failure actions to turn off loading
      // Use actions$ stream instead of store
      this.actions$.pipe(
        ofType(success, failure, ...alsoStopsOn),
        take(1),
        finalize(() => {
          this.state.loading.set(false);
          this.logger.debug(this.name, this.uuid, 'Stop effect loading', propertyKey.toString());
        })
      ).subscribe();
      // Execute the original method (dispatch)
      originalMethod.apply(this, args);
    };

    return descriptor;
  };
}

export function HandleEffectsLoading(
  actions: {success: ActionCreator, failure: ActionCreator}[],
) {
  return function<T extends AbstractBaseComponent>(
    target: Object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(...args: any[]) => void>
  ): TypedPropertyDescriptor<(...args: any[]) => void> {
    const originalMethod = descriptor.value!;
    descriptor.value = function(this: T, ...args: any[]): void {
      // Set loading to true when dispatching
      this.state.loading.set(true);
      this.logger.debug(this.name, this.uuid, 'Start effect loading', propertyKey.toString());
      // Listen for success/failure actions to turn off loading
      // Use actions$ stream instead of store
      const success = actions.map(a => a.success);
      const failure = actions.map(a => a.failure);
      const all = actions.flatMap(a => [a.success, a.failure]);
      this.actions$.pipe(
        ofType(...all),
        take(success.length),
        finalize(() => {
          this.state.loading.set(false);
          this.logger.debug(this.name, this.uuid, 'Stop effect loading', propertyKey.toString());
        })
      ).subscribe();
      // Execute the original method (dispatch)
      originalMethod.apply(this, args);
    };

    return descriptor;
  };
}
